// Unified analytics helper — GA4 + Meta Pixel
// -------------------------------------------
// The gtag loader lives in public/index.html (Measurement ID G-73MKGWZJVV).
// The Meta Pixel base code also lives in public/index.html (Pixel ID 242107385634726).
// This module is the single source of truth for our event schema.

const GA_ID = "G-73MKGWZJVV";
const META_PIXEL_ID = "242107385634726";
const GOOGLE_ADS_ID = "AW-347054205";
// When you create a "Lead Submitted" conversion action in Google Ads,
// paste the conversion label here (looks like "abcDEF123_gh"). Until then
// the Ads conversion event is a no-op but remarketing still works via the
// AW config above.
const GOOGLE_ADS_LEAD_CONVERSION_LABEL = "";

// Local dev logger - noisy in dev, silent in production builds so we never
// leak internal errors or spam users' consoles.
const devWarn = (label, err) => {
  if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`[analytics] ${label}`, err);
  }
};

// -------- GA4 -------------------------------------------------------------
/** Safe wrapper: no-op if gtag isn't loaded (SSR, ad blockers, offline). */
export const gaEvent = (eventName, params = {}) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("event", eventName, { send_to: GA_ID, ...params });
  } catch (err) {
    devWarn(`gaEvent(${eventName}) failed`, err);
  }
};

// -------- Meta Pixel ------------------------------------------------------
/** Safe wrapper for fbq(). No-op if pixel not loaded. */
export const metaEvent = (eventName, params = {}) => {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  try {
    // Standard events accepted by Meta (Lead, Contact, CompleteRegistration
    // etc) go through fbq('track'). Anything else falls back to trackCustom.
    const STANDARD = new Set([
      "Lead", "Contact", "CompleteRegistration", "Subscribe", "ViewContent",
      "AddToCart", "InitiateCheckout", "Purchase", "PageView", "Search",
      "AddPaymentInfo", "AddToWishlist", "Schedule", "SubmitApplication",
    ]);
    if (STANDARD.has(eventName)) {
      window.fbq("track", eventName, params);
    } else {
      window.fbq("trackCustom", eventName, params);
    }
  } catch (err) {
    devWarn(`metaEvent(${eventName}) failed`, err);
  }
};

// -------- Combined lead-submission event ----------------------------------
/**
 * Fires:
 *   1. GA4 `generate_lead` (recommended conversion event) + custom `contact_form_submit`
 *   2. Meta Pixel `Lead` (standard conversion event)
 *
 * Mark `generate_lead` as a key event in GA4 Admin -> Events and set
 * `Lead` as a conversion in Meta Events Manager to attribute campaigns.
 *
 * @param {object} opts
 * @param {string} opts.source     - Which form fired it (e.g. "contact-cta").
 * @param {string} [opts.formName] - Human-readable label for reporting.
 * @param {number} [opts.value]    - Estimated lead value in ZAR (defaults to 500).
 */
export const trackLeadSubmission = ({ source, formName, value = 500 } = {}) => {
  const formLabel = formName || source;
  // GA4 --------------------------------------------------
  gaEvent("generate_lead", {
    currency: "ZAR",
    value,
    lead_source: source,
    form_name: formLabel,
  });
  gaEvent("contact_form_submit", {
    lead_source: source,
    form_name: formLabel,
  });
  // Meta Pixel -------------------------------------------
  metaEvent("Lead", {
    content_name: formLabel,
    content_category: source,
    value,
    currency: "ZAR",
  });
  // Google Ads conversion --------------------------------
  // Only fires once you paste a real conversion label above.
  if (GOOGLE_ADS_LEAD_CONVERSION_LABEL && typeof window !== "undefined" && typeof window.gtag === "function") {
    try {
      window.gtag("event", "conversion", {
        send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_LEAD_CONVERSION_LABEL}`,
        value,
        currency: "ZAR",
        transaction_id: `${source}-${Date.now()}`,
      });
    } catch (err) { devWarn("Ads conversion failed", err); }
  }
};

// -------- Meta Pixel ID export (used by index.html generation) -----------
export const META_PIXEL = META_PIXEL_ID;

// -------- GA4 client_id helper -------------------------------------------
/**
 * Read the GA4 `_ga` cookie and extract the client_id portion.
 * Format is `GA1.<domain-depth>.<client_id-int>.<timestamp>` (e.g.
 * `GA1.1.1234567890.1712345678`). We want `1234567890.1712345678`.
 *
 * Returns `null` if the cookie hasn't been set yet (page just loaded or
 * consent denied). Callers should treat null as "let the server generate one".
 */
export const getGaClientId = () => {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match) return null;
    const parts = decodeURIComponent(match[1]).split(".");
    // Standard cookie format has 4 parts; the client_id is the last two joined.
    if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
    return null;
  } catch (err) {
    devWarn("getGaClientId failed", err);
    return null;
  }
};
